/**
 * Listing Templates — registry, compatibility and platform-default metadata.
 *
 * Single source of truth for the six code-defined template ids, their channel
 * compatibility (eBay / BigCommerce; Amazon is out of scope) and the
 * platform-level default template per channel. This mirrors the backend
 * enforcement in migration 0195 (`listing_template_compatible`,
 * `platform_default_listing_template`) so frontend and backend cannot drift.
 *
 * Template assignment (account default + per-listing override) is persisted in
 * Supabase via `useTemplateAssignments`. The server-side resolution order
 * (listing override → connector default → platform default) is enforced by the
 * `resolve_listing_template` RPC and mirrored client-side by
 * `resolveTemplateForListing`.
 */

// ---------------------------------------------------------------------------
// Product shape (visual/dynamic — bound to canonical data by the adapter)
// ---------------------------------------------------------------------------

export interface ListingTemplateImage {
  src: string;
  alt: string;
}

export interface ListingTemplateSpec {
  label: string;
  value: string;
}

export interface ListingTemplateVariant {
  name: string; // e.g. "Colour" or "Size"
  options: string[];
}

export interface ListingTemplateProduct {
  title: string;
  brand?: string;
  shortDescription: string;
  fullDescription: string[];
  keyFeatures: string[];
  images: ListingTemplateImage[];
  sku: string;
  price: string;
  wasPrice?: string;
  condition: string;
  variants: ListingTemplateVariant[];
  specifications: ListingTemplateSpec[];
  dimensions: string;
  weight: string;
  material: string;
  colour: string;
  size: string;
  warranty?: string;
  shipping: { title: string; lines: string[] };
  returns: { title: string; lines: string[] };
  support: { title: string; lines: string[] };
  rating: { score: number; count: number };
}

// ---------------------------------------------------------------------------
// Template metadata (registry)
// ---------------------------------------------------------------------------

/** A marketplace a template can be rendered for. Amazon is deliberately out of scope. */
export type TemplateChannel = "ebay" | "bigcommerce";

/** The fixed, code-defined template registry ids (single source of truth). */
export const TEMPLATE_IDS = [
  "clean-commerce",
  "premium-brand",
  "marketplace-classic",
  "product-focus",
  "detailed-technical",
  "soft-pastel",
] as const;

export type TemplateId = (typeof TEMPLATE_IDS)[number];

export interface ListingTemplateMeta {
  id: string;
  name: string;
  tagline: string;
  description: string;
  channels: TemplateChannel[];
  /** Short, merchant-friendly use-case tags. */
  suitability: string[];
  /** Brand accent swatch used only by the thumbnail miniature. */
  accent: string;
  /** Muted background tone for the thumbnail. */
  accentSoft: string;
}

export const listingTemplates: ListingTemplateMeta[] = [
  {
    id: "clean-commerce",
    name: "Clean Commerce",
    tagline: "Modern, minimal and easy to read",
    description:
      "A bright, white-space-heavy layout with a strong title, large image gallery, concise feature blocks and clear shipping and support details. Great for everyday retail.",
    channels: ["ebay", "bigcommerce"],
    suitability: ["General retail", "Everyday products", "Clean brand look"],
    accent: "#059669",
    accentSoft: "#ecfdf5",
  },
  {
    id: "premium-brand",
    name: "Premium Brand",
    tagline: "Sophisticated, refined, high-end feel",
    description:
      "A polished presentation with a strong branded header, refined typography, lifestyle imagery, elegant feature highlights and trust sections. Best for higher-value products.",
    channels: ["ebay", "bigcommerce"],
    suitability: ["Luxury & premium", "High-value items", "Established brands"],
    accent: "#b45309",
    accentSoft: "#fffbeb",
  },
  {
    id: "marketplace-classic",
    name: "Marketplace Classic",
    tagline: "Familiar long-form marketplace layout",
    description:
      "An eBay-style long-form listing with a strong title, image presentation, full description, specification table, and shipping, returns and customer-service sections.",
    channels: ["ebay"],
    suitability: ["eBay listings", "Long-form selling", "Trust-focused"],
    accent: "#d97706",
    accentSoft: "#fff7ed",
  },
  {
    id: "product-focus",
    name: "Product Focus",
    tagline: "Image-led with short, punchy copy",
    description:
      "Large product imagery with short copy, prominent key features and compact specifications. Ideal when the visual presentation matters more than long descriptions.",
    channels: ["ebay", "bigcommerce"],
    suitability: ["Visual products", "Fashion & design", "Short descriptions"],
    accent: "#0f766e",
    accentSoft: "#f0fdfa",
  },
  {
    id: "detailed-technical",
    name: "Detailed Technical",
    tagline: "Structured, information-rich and precise",
    description:
      "An information-heavy layout for products with variants, dimensions, material, compatibility and detailed specifications. Perfect for technical or complex products.",
    channels: ["ebay", "bigcommerce"],
    suitability: ["Technical products", "Variants & specs", "B2B / specialist"],
    accent: "#334155",
    accentSoft: "#f1f5f9",
  },
  {
    id: "soft-pastel",
    name: "Soft Pastel",
    tagline: "Airy, light and warm",
    description:
      "A gentle, light-coloured layout with soft pastel accents, rounded cards and generous whitespace. Friendly and approachable — great for home, gift, lifestyle and handmade products.",
    channels: ["ebay", "bigcommerce"],
    suitability: ["Home & gift", "Lifestyle & handmade", "Warm, friendly brands"],
    accent: "#db2777",
    accentSoft: "#fdf2f8",
  },
];

export function templateById(id: string | null | undefined): ListingTemplateMeta | undefined {
  if (!id) return undefined;
  return listingTemplates.find((t) => t.id === id);
}

/** Merchant-friendly channel label: "eBay", "BigCommerce", or "eBay + BigCommerce". */
export function templateChannelLabel(channels: TemplateChannel[]): string {
  const map: Record<TemplateChannel, string> = {
    ebay: "eBay",
    bigcommerce: "BigCommerce",
  };
  return channels.map((c) => map[c]).join(" + ");
}

/** Which templates may be used by a given marketplace provider. */
export function templatesForProvider(provider: string | null | undefined): ListingTemplateMeta[] {
  if (!provider) return listingTemplates;
  return listingTemplates.filter((t) => t.channels.includes(provider as TemplateChannel));
}

// ---------------------------------------------------------------------------
// Platform defaults + compatibility (mirrors the backend migration 0195)
// ---------------------------------------------------------------------------

/** Channel-level fallback used when no connector default or listing override exists. */
export const PLATFORM_DEFAULT_TEMPLATE: Record<TemplateChannel, TemplateId> = {
  ebay: "marketplace-classic",
  bigcommerce: "clean-commerce",
};

export function platformDefaultTemplate(provider: string | null | undefined): TemplateId | undefined {
  if (!provider) return undefined;
  return PLATFORM_DEFAULT_TEMPLATE[provider as TemplateChannel];
}

/** Whether a string is one of the six built-in template ids. */
export function isValidTemplateId(id: string | null | undefined): id is TemplateId {
  return !!id && (TEMPLATE_IDS as readonly string[]).includes(id);
}

/** Whether a template id is compatible with a marketplace provider (Amazon unsupported). */
export function isTemplateCompatibleWithProvider(
  templateId: string | null | undefined,
  provider: string | null | undefined,
): boolean {
  if (!templateId || !provider) return false;
  const meta = templateById(templateId);
  if (!meta) return false;
  return (meta.channels as string[]).includes(provider);
}